import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "../lib/content";

const menuVariants = {
  closed: { clipPath: "circle(0% at calc(100% - 44px) 44px)" },
  open: {
    clipPath: "circle(150% at calc(100% - 44px) 44px)",
    transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] },
  },
};

const linkVariants = {
  closed: { y: 60, opacity: 0 },
  open: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.25 + i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "backdrop-blur-xl bg-white/80 border-b border-black/5 py-4" : "bg-transparent py-6"
        }`}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between">
          <Logo variant="dark" textClass="text-2xl" />
          <button
            data-testid="menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="relative z-[60] inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white/70 px-4 py-2.5 text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
          >
            <span className="hidden sm:block text-sm font-medium tracking-wide">
              {open ? "Close" : "Menu"}
            </span>
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="fixed inset-0 z-40 bg-navy grain flex flex-col justify-center px-6 lg:px-10"
          >
            <div className="mx-auto max-w-7xl w-full">
              <p className="mb-10 text-xs uppercase tracking-[0.3em] text-gold">Navigation</p>
              <ul className="space-y-2">
                {NAV_LINKS.map((link, i) => (
                  <li key={link.to} className="overflow-hidden">
                    <motion.div custom={i} variants={linkVariants} initial="closed" animate="open">
                      <Link
                        to={link.to}
                        data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        className="group flex items-center gap-4 py-2 font-display text-4xl sm:text-6xl lg:text-7xl font-medium text-white/90 transition-colors duration-300 hover:text-gold"
                      >
                        <span className="text-base font-body text-white/30 group-hover:text-gold transition-colors">
                          0{i + 1}
                        </span>
                        {link.label}
                        <ArrowUpRight className="opacity-0 -translate-x-3 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-gold" size={40} />
                      </Link>
                    </motion.div>
                  </li>
                ))}
              </ul>
              <motion.div
                custom={NAV_LINKS.length}
                variants={linkVariants}
                initial="closed"
                animate="open"
                className="mt-14 border-t border-white/10 pt-8 text-white/60 text-sm"
              >
                A Division of Praxio (Pty) Ltd · Midrand, South Africa
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
